/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Composer, { BaseComposerArgs } from 'cio/components/action-composer/base-glimmer';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { equal, alias } from '@ember/object/computed';
import { isEmpty, isBlank, isNone } from '@ember/utils';
import DS from 'ember-data';
import EmailPreviewService from 'cio/services/email-preview';
import EditorService from 'cio/services/editor';
import TemplateModel from 'cio/models/template';
import LayoutModel from 'cio/models/layout';
import { ComposerSection } from 'cio/components/action-composer';
import AnalyticsService from 'cio/services/analytics';
import { EmailPreview } from 'cio/utils/previews';

interface ComposerArgs extends BaseComposerArgs {
  preview: EmailPreview;
}

interface ComposerSignature {
  Args: ComposerArgs;
  Element: HTMLDivElement;
}

export default class TextAndCode extends Composer<ComposerSignature> {
  @service analytics!: AnalyticsService;
  @service editor!: EditorService;
  @service emailPreview!: EmailPreviewService;
  @service store!: DS.Store;

  previewURL = 'previews/email';

  previewObserverKeys = [
    'args.editableEntity.template.{bcc,body,fromIdentity,recipient,replyToIdentity,subject,preheaderText}',
    'args.editableEntity.template.headers.@each.{name,value}',
  ];

  @tracked originalFromIdentity?: TemplateModel['fromIdentity'];
  @tracked originalReplyToIdentity?: TemplateModel['replyToIdentity'];

  @tracked originalLayoutId?: LayoutModel['id'];
  @tracked selectedLayout?: LayoutModel;
  @tracked isChoosingLayout = false;

  @equal('args.editorName', 'bee') isBeeEditor!: boolean;
  @alias('hasHeaderErrors') isInvalid!: boolean;

  @computed(
    'template.{fromIdentity.id,replyToIdentity.id,layout.id,hasDirtyAttributes,body,bodyJson}',
    'originalFromIdentity.id',
    'originalReplyToIdentity.id',
    'originalLayoutId',
    'isBeeEditor',
  )
  get isDirty() {
    let { isBeeEditor } = this;
    let layoutId = this.template.layout?.get('id');

    let isFromDirty = this.originalFromIdentity?.id !== this.template.fromIdentity?.get('id');
    let isReplyToDirty = this.originalReplyToIdentity?.id !== this.template.replyToIdentity?.get('id');
    let isLayoutDirty = this.originalLayoutId !== layoutId;
    let isTemplateDirty = this.template.get('hasDirtyAttributes');

    // For Bee, specifically, we expect the layout to be null, so we don't mark that as dirty
    if (isBeeEditor && isNone(layoutId)) {
      isLayoutDirty = false;
      isTemplateDirty = this.template.get('hasDirtyAttributes');
    }

    let areRelationshipsDirty = isFromDirty || isReplyToDirty || isLayoutDirty;
    return areRelationshipsDirty || isTemplateDirty;
  }

  @computed('args.preview.errors.headers.[]')
  get hasHeaderErrors() {
    let errors = this.args.preview?.errors?.headers || [];
    return errors.some((error: any) => !isEmpty(error.name) || !isEmpty(error.value));
  }

  @computed('hasHeaderErrors')
  get saveTooltipContent() {
    if (this.hasHeaderErrors) {
      return 'Please fix any errors in your email headers to save your message.';
    }

    return null;
  }

  constructor(owner: unknown, args: ComposerSignature['Args']) {
    super(owner, args);
    this.watchTemplate();
    this.selectedLayout = this.template.layout;
    this.cacheOriginalRelationships();
  }

  getPreviewData() {
    let layout = this.selectedLayout;
    let fields = this.emailPreview.generateTemplateData(this.template, layout, this.isBeeEditor);

    return fields;
  }

  @action
  showLayoutPicker() {
    this.args.toggleView(ComposerSection.Preview);
    this.isChoosingLayout = true;
    this.analytics.track('email', 'change_layout_clicked', this.args.editableEntity.analyticsData);
  }

  @action
  rollback() {
    let originalLayout = this.originalLayoutId ? this.store.peekRecord('layout', this.originalLayoutId)! : undefined;

    this.template.rollbackAttributes();
    this.template.setProperties({
      fromIdentity: this.originalFromIdentity,
      layout: originalLayout,
      replyToIdentity: this.originalReplyToIdentity,
    });

    this.selectedLayout = originalLayout;
    return this.args.onRollback();
  }

  @action
  save() {
    this.removeEmptyHeaders();

    if (!this.template.editor) {
      this.template.set('editor', this.args.editorName);
    }

    let promise = this.args.onSave();

    promise.then(
      () => {
        this.cacheOriginalRelationships();
      },
      () => {},
    );

    return promise;
  }

  @action
  discardLayoutSelection() {
    this.isChoosingLayout = false;
    this.selectedLayout = this.template.layout;
  }

  @action
  saveLayout() {
    this.template.set('layout', this.selectedLayout);

    this.isChoosingLayout = false;
    this.analytics.track('email', 'change_layout_applied', this.args.editableEntity.analyticsData);
  }

  @action
  selectLayout(layout: LayoutModel) {
    this.selectedLayout = layout;
  }

  removeEmptyHeaders() {
    let toRemove = this.template.headers.filter((header) => isBlank(header.name) && isBlank(header.value));
    this.template.headers.removeObjects(toRemove);
  }

  cacheOriginalRelationships() {
    this.originalFromIdentity = this.template.get('fromIdentity');
    this.originalReplyToIdentity = this.template.get('replyToIdentity');
    this.originalLayoutId = this.template.layout?.get('id');
  }

  @action
  onPreviewEmail() {
    this.args.onFetchPreview(this.previewURL, () => this.getPreviewData());
  }

  @action
  onTemplateSwitch() {
    this.selectedLayout = this.template.layout;
    this.isChoosingLayout = false;
    this.cacheOriginalRelationships();
  }
}
